import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../contexts/UserAuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatDateTime, formatRelativeTime } from '../../utils/timezoneUtils';

interface Ticket {
  id: number;
  status: string;
  created_at: string;
  updated_at: string;
  last_reply_at: string;
  last_reply_sender: string;
  reply_count: number;
  unread_replies: number;
  request: {
    id: number;
    number: string;
    company_name: string;
    package_name: string;
    country_name: string;
    country_flag: string;
  };
}

const Tickets: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { } = useUserAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  const getCountryFlagImage = (countryFlag: string, countryName: string) => {
    const isEmoji = countryFlag && countryFlag.length > 2;
    const fallbackEmoji = isEmoji ? countryFlag : '🌍';
    const countryCode = isEmoji ? '' : countryFlag.toUpperCase();

    if (countryCode && countryCode.length <= 3) {
      return (
        <img
          src={'https://flagcdn.com/48x36/' + countryCode.toLowerCase() + '.png'}
          alt={`${countryName} flag`}
          className="w-5 h-3 object-cover rounded-sm inline"
          onError={(e) => {
            const target = e.currentTarget;
            target.style.display = 'none';
            const parent = target.parentNode as HTMLElement;
            if (parent && !parent.querySelector('.flag-fallback')) {
              const span = document.createElement('span');
              span.className = 'flag-fallback text-lg';
              span.textContent = fallbackEmoji;
              parent.appendChild(span);
            }
          }}
        />
      );
    }

    return <span className="text-lg">{fallbackEmoji}</span>;
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://patrixglobal.com/backend/api/tickets/list.php', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }

      const data = await response.json();
      if (data.success) {
        setTickets(data.tickets || []);
      } else {
        setError(data.error || 'Failed to load tickets');
      }
    } catch (error) {
      setError('Error loading tickets');    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      'waiting_for_client': 'bg-yellow-100 text-yellow-800',
      'answered': 'bg-green-100 text-green-800',
      'processing': 'bg-blue-100 text-blue-800',
      'closed': 'bg-gray-100 text-gray-800',
    };

    const statusTranslationMap = {
      'waiting_for_client': 'waitingForClient',
      'answered': 'answered',
      'processing': 'processing',
      'closed': 'closed',
    };

    const normalizedStatus = status.toLowerCase();
    const translationKey = statusTranslationMap[normalizedStatus as keyof typeof statusTranslationMap] || normalizedStatus;
    const cssClass = statusClasses[normalizedStatus as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cssClass}`}>
        {t(`dashboard.tickets.${translationKey}`) || status}
      </span>
    );
  };

  const formatDateDisplay = (dateString: string) => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en-US';
    return formatDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, locale);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('dashboard.tickets.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('dashboard.tickets.myTickets')}
          </p>
        </div>
        
        <Link
          to="/dashboard/tickets/create"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('dashboard.tickets.create')}
        </Link>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {tickets.length === 0 && !error ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('dashboard.tickets.noTickets')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('dashboard.tickets.createFirst')}</p>
          <div className="mt-6">
            <Link
              to="/dashboard/tickets/create"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              {t('dashboard.tickets.create')}
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <Link
                  to={`/dashboard/tickets/${ticket.id}`}
                  className="block hover:bg-gray-50 px-4 py-4 sm:px-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            Ticket #{ticket.id}
                          </p>
                          <div className="ml-2">
                            {getStatusBadge(ticket.status)}
                          </div>
                          {ticket.unread_replies > 0 && (
                            <div className="ml-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {ticket.unread_replies} New
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="mt-1">
                          <p className="text-sm text-gray-500">
                            {getCountryFlagImage(ticket.request.country_flag, ticket.request.country_name)} {ticket.request.country_name} - {ticket.request.package_name}
                          </p>
                          {ticket.request.company_name && (
                            <p className="text-sm text-gray-400">
                              {ticket.request.company_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {t('dashboard.tickets.lastUpdate')}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDateDisplay(ticket.updated_at)}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Tickets;